import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-background/55 backdrop-blur-md',
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  className?: string;
  /** `wide`: ocupa quase toda a tela (edição densa, listas longas); o conteúdo rola por dentro. */
  size?: 'default' | 'wide';
}

const SIZE_CLASSES: Record<NonNullable<DialogContentProps['size']>, string> = {
  default: 'max-w-lg',
  wide: 'max-w-6xl content-start sm:h-[calc(100dvh-4rem)] sm:p-8',
};

/**
 * Dialog centralizado sem `transform` no painel.
 * Transform + teclado no iOS quebra hit-testing/foco (toques voltam ao input anterior).
 */
const DialogContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size = 'default', ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogOverlay />
    {/*
      Wrappers com pointer-events-none: cliques fora do painel passam para o Overlay (fechar).
      O painel (Content) reativa pointer-events.
    */}
    <div className="pointer-events-none fixed inset-0 z-50 overflow-y-auto overscroll-contain">
      <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            /* min-w-0: sem isso, um filho com conteúdo intrínseco largo (ex.: HTML injetado sem espaços) força a coluna do grid a crescer além do max-w-lg, mesmo com overflow-x-hidden no filho. */
            'pointer-events-auto glass-elevated glass-sheen relative z-50 grid w-full min-w-0 max-h-[calc(100dvh-1.5rem)] gap-4 overflow-y-auto rounded-2xl p-6 sm:max-h-[calc(100dvh-2rem)]',
            SIZE_CLASSES[size],
            /* Só fade — zoom usa transform e conflita com foco/toque no mobile. */
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'duration-200',
            className,
          )}
          {...props}
        >
          {children}
          <DialogPrimitive.Close className="absolute right-4 top-4 grid size-8 place-items-center rounded-lg text-muted-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="size-4" />
            <span className="sr-only">Fechar</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </div>
    </div>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1.5 pr-8 text-left', className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mt-1 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
